import React, { useEffect, useState } from 'react';
import { 
  getInvoices, 
  createInvoice, 
  approveInvoice, 
  sendInvoice, 
  recordPayment, 
  getCompanies,
  generateInvoicePDF,
  getContracts,
  getInvoicesV2,
  approveInvoiceV2,
  sendInvoiceV2,
  getInvoicePDF
} from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Receipt, 
  Send, 
  CheckCircle2, 
  DollarSign, 
  AlertCircle,
  FileText,
  Download,
  Eye,
  Printer,
  Languages,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const statusConfig = {
  draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
  pending_approval: { color: 'bg-amber-100 text-amber-800', label: 'Pending Approval' },
  approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
  sent: { color: 'bg-blue-100 text-blue-800', label: 'Sent' },
  paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
  overdue: { color: 'bg-red-100 text-red-800', label: 'Overdue' },
};

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [invoicesV2, setInvoicesV2] = useState([]);
  const [activeTab, setActiveTab] = useState('operations');
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  
  // PDF Generation state
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfData, setPdfData] = useState({
    // Company details
    company_name_en: '',
    company_name_ar: '',
    company_vat_number: '',
    company_crn: '',
    company_street: '',
    company_building: '',
    company_postal_code: '',
    company_city: '',
    // Client details
    client_name_en: '',
    client_name_ar: '',
    client_vat_number: '',
    client_crn: '',
    client_street: '',
    // Invoice info
    invoice_number: '',
    invoice_date: '',
    supply_date: '',
    due_date: '',
    po_number: '',
    project_reference: '',
    period_from: '',
    period_to: '',
    // Items
    items: [{
      description_en: '',
      description_ar: '',
      quantity: 1,
      unit: 'Month',
      unit_rate: 0,
      code: ''
    }],
    // Totals
    subtotal: 0,
    vat_rate: 15,
    vat_amount: 0,
    total: 0,
    // Bank details
    bank_name: 'Al Rajhi Bank',
    bank_account_name: '',
    bank_account_number: '',
    bank_iban: '',
    bank_branch: ''
  });

  useEffect(() => {
    loadCompanies();
    loadInvoices();
    loadContracts();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadContracts = async () => {
    try {
      const response = await getContracts();
      setContracts(response.data);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const [v1Res, v2Res] = await Promise.all([
        getInvoices().catch(() => ({ data: [] })),
        getInvoicesV2().catch(() => ({ data: [] }))
      ]);
      setInvoices(v1Res.data);
      setInvoicesV2(v2Res.data || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveInvoice(id);
      toast.success('Invoice approved!');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to approve invoice');
    }
  };

  const handleSend = async (id) => {
    try {
      await sendInvoice(id);
      toast.success('Invoice sent to client!');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleApproveV2 = async (id) => {
    try {
      await approveInvoiceV2(id);
      toast.success('Invoice approved');
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve invoice');
    }
  };

  const handleSendV2 = async (id) => {
    try {
      await sendInvoiceV2(id);
      toast.success('Invoice marked as sent');
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleDownloadV2PDF = async (inv) => {
    try {
      const res = await getInvoicePDF(inv.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${inv.invoice_number}.pdf`;
      a.click();
    } catch (error) {
      toast.error('Failed to download invoice PDF');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.total.toString());
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter valid payment amount');
      return;
    }
    try {
      await recordPayment(selectedInvoice.id, parseFloat(paymentAmount));
      toast.success('Payment recorded successfully!');
      setPaymentDialogOpen(false);
      loadInvoices();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // PDF Generation Functions
  const openPdfDialog = (invoice) => {
    const company = companies.find(c => c.id === invoice.company_id);
    const contract = contracts.find(c => c.id === invoice.contract_id);
    
    // Pre-fill PDF data from invoice
    setPdfData({
      // Company details
      company_name_en: company?.name || 'Your Company Name',
      company_name_ar: company?.name_ar || 'اسم شركتك',
      company_vat_number: company?.vat_number || '',
      company_crn: company?.registration_number || '',
      company_street: company?.street || 'Street Address',
      company_building: company?.building_no || '',
      company_postal_code: company?.postal_code || '',
      company_city: company?.city || 'Riyadh',
      // Client details
      client_name_en: invoice.client_name || '',
      client_name_ar: contract?.client_name_ar || '',
      client_vat_number: contract?.client_vat_number || '',
      client_crn: contract?.client_crn || '',
      client_street: contract?.client_address || '',
      // Invoice info
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date?.split('T')[0] || '',
      supply_date: invoice.invoice_date?.split('T')[0] || '',
      due_date: invoice.due_date?.split('T')[0] || '',
      po_number: contract?.po_number || '',
      project_reference: contract?.project_reference || contract?.contract_number || '',
      period_from: '',
      period_to: '',
      // Items
      items: [{
        description_en: `Rental services as per contract ${contract?.contract_number || ''}`,
        description_ar: 'خدمات التأجير حسب العقد',
        quantity: 1,
        unit: 'Month',
        unit_rate: invoice.subtotal,
        code: invoice.invoice_number
      }],
      // Totals
      subtotal: invoice.subtotal,
      vat_rate: company?.vat_rate || 15,
      vat_amount: invoice.vat_amount,
      total: invoice.total,
      // Bank details
      bank_name: company?.bank_name || 'Al Rajhi Bank',
      bank_account_name: company?.name || '',
      bank_account_number: company?.bank_account || '',
      bank_iban: company?.bank_iban || '',
      bank_branch: company?.bank_branch || ''
    });
    
    setSelectedInvoice(invoice);
    setPdfDialogOpen(true);
  };

  const handleGeneratePDF = async () => {
    setPdfLoading(true);
    try {
      const response = await generateInvoicePDF(pdfData);
      
      if (response.data.success && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `Invoice_${pdfData.invoice_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('PDF generated and downloaded!');
      }
    } catch (error) {
      toast.error('Failed to generate PDF: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPdfLoading(false);
    }
  };

  const updatePdfItem = (index, field, value) => {
    const newItems = [...pdfData.items];
    newItems[index][field] = value;
    
    // Recalculate totals
    const subtotal = newItems.reduce((sum, item) => sum + (item.quantity * item.unit_rate), 0);
    const vat_amount = subtotal * (pdfData.vat_rate / 100);
    const total = subtotal + vat_amount;
    
    setPdfData({
      ...pdfData,
      items: newItems,
      subtotal,
      vat_amount,
      total
    });
  };

  const addPdfItem = () => {
    setPdfData({
      ...pdfData,
      items: [...pdfData.items, {
        description_en: '',
        description_ar: '',
        quantity: 1,
        unit: 'Month',
        unit_rate: 0,
        code: ''
      }]
    });
  };

  const removePdfItem = (index) => {
    if (pdfData.items.length > 1) {
      const newItems = pdfData.items.filter((_, i) => i !== index);
      const subtotal = newItems.reduce((sum, item) => sum + (item.quantity * item.unit_rate), 0);
      const vat_amount = subtotal * (pdfData.vat_rate / 100);
      const total = subtotal + vat_amount;
      
      setPdfData({
        ...pdfData,
        items: newItems,
        subtotal,
        vat_amount,
        total
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="invoices-title">Invoices</h1>
          <p className="text-muted-foreground mt-1">{invoices.length + invoicesV2.length} total invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Languages className="h-3 w-3" />
            Bilingual PDF Support
          </Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Invoiced</div>
          <div className="text-2xl font-bold" data-testid="total-invoiced">
            {invoices.reduce((sum, inv) => sum + inv.total, 0).toLocaleString()} SAR
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Paid</div>
          <div className="text-2xl font-bold text-green-600" data-testid="total-paid">
            {invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0).toLocaleString()} SAR
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Pending</div>
          <div className="text-2xl font-bold text-amber-600" data-testid="total-pending">
            {invoices.filter(inv => inv.status === 'sent').reduce((sum, inv) => sum + inv.total, 0).toLocaleString()} SAR
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Overdue</div>
          <div className="text-2xl font-bold text-red-600" data-testid="total-overdue">
            {invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.total, 0).toLocaleString()} SAR
          </div>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading invoices...</div>
        </div>
      ) : (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="operations" data-testid="operations-invoices-tab">
            Operations Invoices ({invoicesV2.length})
          </TabsTrigger>
          <TabsTrigger value="contract" data-testid="contract-invoices-tab">
            Contract Invoices ({invoices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="operations" className="pt-4">
          {invoicesV2.length === 0 ? (
            <Card className="p-12 text-center">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No operations invoices yet</h3>
              <p className="text-muted-foreground">Invoices are auto-generated when client timesheets are approved (Equipment &amp; Manpower), or from Material DNs and Transport trips</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {invoicesV2.map((inv) => (
                <Card key={inv.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`invoice-v2-card-${inv.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-accent/10 rounded-lg">
                        <Receipt className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-bold" data-testid={`invoice-v2-number-${inv.id}`}>{inv.invoice_number}</h3>
                        <p className="text-sm text-muted-foreground">{inv.client_name}</p>
                      </div>
                    </div>
                    <Badge className={statusConfig[inv.status]?.color || 'bg-gray-100 text-gray-800'} data-testid={`invoice-v2-status-${inv.id}`}>
                      {statusConfig[inv.status]?.label || inv.status}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-bold text-lg">{(inv.total || 0).toLocaleString()} {inv.currency || 'SAR'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="capitalize">{inv.invoice_type}</span>
                    </div>
                    {inv.dn_number && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Delivery Note:</span>
                        <span>{inv.dn_number}</span>
                      </div>
                    )}
                    {inv.timesheet_number && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Timesheet:</span>
                        <span>{inv.timesheet_number}</span>
                      </div>
                    )}
                    {inv.period_from && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Period:</span>
                        <span>{inv.period_from} → {inv.period_to}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadV2PDF(inv)}
                      data-testid={`download-v2-pdf-${inv.id}`}
                      className="gap-1"
                    >
                      <Languages className="h-4 w-4" />
                      ZATCA PDF
                    </Button>
                    {inv.status === 'pending_approval' && (
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproveV2(inv.id)}
                        data-testid={`approve-invoice-v2-${inv.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                    )}
                    {inv.status === 'approved' && (
                      <Button
                        size="sm"
                        className="flex-1 bg-accent hover:bg-accent/90"
                        onClick={() => handleSendV2(inv.id)}
                        data-testid={`send-invoice-v2-${inv.id}`}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Send
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contract" className="pt-4">
      {invoices.length === 0 ? (
        <Card className="p-12 text-center">
          <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No invoices yet</h3>
          <p className="text-muted-foreground">Invoices are generated from approved timesheets</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {invoices.map((invoice) => {
            const company = companies.find(c => c.id === invoice.company_id);
            const dueDate = new Date(invoice.due_date);
            const isOverdue = dueDate < new Date() && invoice.status !== 'paid';
            
            return (
              <Card key={invoice.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`invoice-card-${invoice.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-accent/10 rounded-lg">
                      <Receipt className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-bold" data-testid={`invoice-number-${invoice.id}`}>{invoice.invoice_number}</h3>
                      <p className="text-sm text-muted-foreground">{invoice.client_name}</p>
                    </div>
                  </div>
                  <Badge className={statusConfig[isOverdue && invoice.status !== 'paid' ? 'overdue' : invoice.status]?.color} data-testid={`invoice-status-${invoice.id}`}>
                    {statusConfig[isOverdue && invoice.status !== 'paid' ? 'overdue' : invoice.status]?.label}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-bold text-lg">{invoice.total.toLocaleString()} {company?.currency || 'SAR'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Invoice Date:</span>
                    <span>{new Date(invoice.invoice_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Due Date:</span>
                    <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                      {dueDate.toLocaleDateString()}
                    </span>
                  </div>
                  {invoice.paid_at && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Paid On:</span>
                      <span className="text-green-600">{new Date(invoice.paid_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  {/* PDF Download Button - Always visible */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPdfDialog(invoice)}
                    data-testid={`download-pdf-${invoice.id}`}
                    className="gap-1"
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </Button>

                  {invoice.status === 'draft' && (
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(invoice.id)}
                      data-testid={`approve-invoice-${invoice.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  )}
                  {invoice.status === 'approved' && (
                    <Button
                      size="sm"
                      className="flex-1 bg-accent hover:bg-accent/90"
                      onClick={() => handleSend(invoice.id)}
                      data-testid={`send-invoice-${invoice.id}`}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Send
                    </Button>
                  )}
                  {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => openPaymentDialog(invoice)}
                      data-testid={`record-payment-${invoice.id}`}
                    >
                      <DollarSign className="h-4 w-4 mr-1" />
                      Record Payment
                    </Button>
                  )}
                  {invoice.status === 'paid' && (
                    <div className="text-sm text-green-600 flex items-center gap-1 flex-1 justify-center">
                      <CheckCircle2 className="h-4 w-4" />
                      Paid
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
        </TabsContent>
      </Tabs>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Invoice Number</Label>
              <Input value={selectedInvoice?.invoice_number} disabled />
            </div>
            <div>
              <Label>Payment Amount (SAR) *</Label>
              <Input
                type="number"
                min="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                data-testid="payment-amount-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} data-testid="cancel-payment-button">Cancel</Button>
              <Button onClick={handleRecordPayment} className="bg-green-600 hover:bg-green-700" data-testid="confirm-payment-button">
                Record Payment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Generation Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Generate Bilingual Invoice PDF
            </DialogTitle>
            <DialogDescription>
              Generate Arabic/English tax invoice compliant with ZATCA requirements
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="company" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="company">Company</TabsTrigger>
              <TabsTrigger value="client">Client</TabsTrigger>
              <TabsTrigger value="items">Line Items</TabsTrigger>
              <TabsTrigger value="bank">Bank & Preview</TabsTrigger>
            </TabsList>

            {/* Company Details Tab */}
            <TabsContent value="company" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company Name (English) *</Label>
                  <Input
                    value={pdfData.company_name_en}
                    onChange={(e) => setPdfData({...pdfData, company_name_en: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Company Name (Arabic) *</Label>
                  <Input
                    value={pdfData.company_name_ar}
                    onChange={(e) => setPdfData({...pdfData, company_name_ar: e.target.value})}
                    dir="rtl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>VAT Number *</Label>
                  <Input
                    value={pdfData.company_vat_number}
                    onChange={(e) => setPdfData({...pdfData, company_vat_number: e.target.value})}
                    placeholder="300XXXXXXXXXX003"
                  />
                </div>
                <div>
                  <Label>Commercial Registration (CR)</Label>
                  <Input
                    value={pdfData.company_crn}
                    onChange={(e) => setPdfData({...pdfData, company_crn: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Street Address</Label>
                  <Input
                    value={pdfData.company_street}
                    onChange={(e) => setPdfData({...pdfData, company_street: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Building No.</Label>
                  <Input
                    value={pdfData.company_building}
                    onChange={(e) => setPdfData({...pdfData, company_building: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Postal Code</Label>
                  <Input
                    value={pdfData.company_postal_code}
                    onChange={(e) => setPdfData({...pdfData, company_postal_code: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={pdfData.company_city}
                  onChange={(e) => setPdfData({...pdfData, company_city: e.target.value})}
                />
              </div>
            </TabsContent>

            {/* Client Details Tab */}
            <TabsContent value="client" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client Name (English) *</Label>
                  <Input
                    value={pdfData.client_name_en}
                    onChange={(e) => setPdfData({...pdfData, client_name_en: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Client Name (Arabic)</Label>
                  <Input
                    value={pdfData.client_name_ar}
                    onChange={(e) => setPdfData({...pdfData, client_name_ar: e.target.value})}
                    dir="rtl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client VAT Number</Label>
                  <Input
                    value={pdfData.client_vat_number}
                    onChange={(e) => setPdfData({...pdfData, client_vat_number: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Client CR Number</Label>
                  <Input
                    value={pdfData.client_crn}
                    onChange={(e) => setPdfData({...pdfData, client_crn: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Client Address</Label>
                <Input
                  value={pdfData.client_street}
                  onChange={(e) => setPdfData({...pdfData, client_street: e.target.value})}
                />
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3">Invoice Details</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Invoice Date *</Label>
                    <Input
                      type="date"
                      value={pdfData.invoice_date}
                      onChange={(e) => setPdfData({...pdfData, invoice_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Supply Date</Label>
                    <Input
                      type="date"
                      value={pdfData.supply_date}
                      onChange={(e) => setPdfData({...pdfData, supply_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Due Date *</Label>
                    <Input
                      type="date"
                      value={pdfData.due_date}
                      onChange={(e) => setPdfData({...pdfData, due_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label>PO Number</Label>
                    <Input
                      value={pdfData.po_number}
                      onChange={(e) => setPdfData({...pdfData, po_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Project Reference</Label>
                    <Input
                      value={pdfData.project_reference}
                      onChange={(e) => setPdfData({...pdfData, project_reference: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Line Items Tab */}
            <TabsContent value="items" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Line Items</h4>
                <Button variant="outline" size="sm" onClick={addPdfItem}>
                  Add Item
                </Button>
              </div>
              
              {pdfData.items.map((item, idx) => (
                <Card key={idx} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-semibold text-sm">Item {idx + 1}</span>
                    {pdfData.items.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 h-6 px-2"
                        onClick={() => removePdfItem(idx)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Description (English)</Label>
                      <Textarea
                        value={item.description_en}
                        onChange={(e) => updatePdfItem(idx, 'description_en', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description (Arabic)</Label>
                      <Textarea
                        value={item.description_ar}
                        onChange={(e) => updatePdfItem(idx, 'description_ar', e.target.value)}
                        rows={2}
                        dir="rtl"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-3">
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updatePdfItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => updatePdfItem(idx, 'unit', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit Rate (SAR)</Label>
                      <Input
                        type="number"
                        value={item.unit_rate}
                        onChange={(e) => updatePdfItem(idx, 'unit_rate', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Code/SKU</Label>
                      <Input
                        value={item.code}
                        onChange={(e) => updatePdfItem(idx, 'code', e.target.value)}
                      />
                    </div>
                  </div>
                </Card>
              ))}

              {/* Totals Summary */}
              <Card className="p-4 bg-slate-50">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal (excl. VAT):</span>
                    <span className="font-mono">{pdfData.subtotal.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT ({pdfData.vat_rate}%):</span>
                    <span className="font-mono">{pdfData.vat_amount.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total (incl. VAT):</span>
                    <span className="font-mono">{pdfData.total.toLocaleString()} SAR</span>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Bank & Preview Tab */}
            <TabsContent value="bank" className="space-y-4 mt-4">
              <h4 className="font-semibold">Bank Payment Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={pdfData.bank_name}
                    onChange={(e) => setPdfData({...pdfData, bank_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Account Name</Label>
                  <Input
                    value={pdfData.bank_account_name}
                    onChange={(e) => setPdfData({...pdfData, bank_account_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Account Number</Label>
                  <Input
                    value={pdfData.bank_account_number}
                    onChange={(e) => setPdfData({...pdfData, bank_account_number: e.target.value})}
                  />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <Input
                    value={pdfData.bank_iban}
                    onChange={(e) => setPdfData({...pdfData, bank_iban: e.target.value})}
                    placeholder="SA..."
                  />
                </div>
              </div>
              <div>
                <Label>Branch</Label>
                <Input
                  value={pdfData.bank_branch}
                  onChange={(e) => setPdfData({...pdfData, bank_branch: e.target.value})}
                />
              </div>

              {/* Invoice Summary Preview */}
              <Card className="p-4 mt-6 bg-gradient-to-r from-slate-50 to-slate-100">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Invoice Preview
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Invoice Number</p>
                    <p className="font-semibold">{pdfData.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Client</p>
                    <p className="font-semibold">{pdfData.client_name_en}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Invoice Date</p>
                    <p className="font-semibold">{pdfData.invoice_date}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-semibold">{pdfData.due_date}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Total Amount</p>
                    <p className="font-bold text-2xl text-accent">{pdfData.total.toLocaleString()} SAR</p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <Languages className="h-4 w-4" />
                    <span>PDF will include Arabic & English text with ZATCA QR code</span>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setPdfDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGeneratePDF} 
              disabled={pdfLoading}
              className="bg-accent gap-2"
              data-testid="generate-pdf-btn"
            >
              {pdfLoading ? (
                <>Generating...</>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Generate & Download PDF
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
